/* ============================================================
   PintaFlow — Design Direction V2: "Studio Orange"
   ----------------------------------------------------------
   - Color: Black + Ivory + Signal Orange (#FF5B1F)
   - Feel: Editorial magazine × creator studio × Apple Music vibe
   - Type: Fraunces (display, expressive serif) + Inter (body)
   - Hero: Pipeline as a horizontal "ride" with a moving card
   ============================================================ */
import { ArrowRight, Asterisk } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STAGES = ["Idea", "Research", "Script", "Create", "Schedule", "Published"];

export default function LandingV2() {
  return (
    <div
      className="min-h-screen bg-[#0B0A09] text-[#F5F0E6] overflow-x-hidden"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* Top bar */}
      <header className="border-b border-[#F5F0E6]/10">
        <div className="max-w-[1280px] mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-[#FF5B1F] flex items-center justify-center">
              <Asterisk size={14} className="text-black" strokeWidth={3} />
            </div>
            <span
              className="text-xl font-semibold tracking-tight"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32" }}
            >
              PintaFlow
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#F5F0E6]/60">
            <span className="hidden md:inline tracking-tight">Issue 01 · Creator OS</span>
            <button onClick={startGoogleLogin} className="text-[#F5F0E6] hover:text-[#FF5B1F] transition">
              Sign in →
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 pt-16 md:pt-24 pb-10">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-9">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#FF5B1F] mb-7 font-medium">
              Vol. 01 — The Creator&apos;s Operating System
            </div>
            <h1
              className="text-[56px] sm:text-[88px] md:text-[112px] leading-[0.9] tracking-[-0.04em] font-medium"
              style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 120" }}
            >
              Make the <em className="text-[#FF5B1F] not-italic">work</em> <br />
              the <span className="italic" style={{ fontVariationSettings: "'opsz' 144" }}>
                main
              </span> thing.
            </h1>
          </div>
          <div className="col-span-12 md:col-span-3">
            <p className="text-base text-[#F5F0E6]/70 leading-relaxed max-w-xs">
              An opinionated workspace where every idea lives, ships, and stays connected to the next one.
              Built for creators who treat the work like the craft it is.
            </p>
            <button
              onClick={startGoogleLogin}
              className="mt-6 inline-flex items-center gap-2 bg-[#FF5B1F] text-black px-5 py-3 rounded-full font-medium text-sm hover:bg-[#F5F0E6] transition"
            >
              Start with Google <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* PIPELINE as marquee ride */}
      <section className="relative border-y border-[#F5F0E6]/10 bg-[#100E0C] py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#F5F0E6]/40 mb-2">The Pipeline</div>
              <h2
                className="text-3xl md:text-5xl tracking-tight"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                One ride. From spark to publish.
              </h2>
            </div>
            <div className="text-sm text-[#F5F0E6]/50 max-w-xs">
              Drag a card from left to right. PintaFlow handles the rest — auto-tasks, schedule, publish timestamp.
            </div>
          </div>

          {/* Stage rail */}
          <div className="relative">
            <div className="absolute left-0 right-0 top-[26px] h-px bg-gradient-to-r from-transparent via-[#FF5B1F]/60 to-transparent" />
            <div className="grid grid-cols-6 gap-2 relative">
              {STAGES.map((s, i) => (
                <div key={s} className="flex flex-col items-center relative">
                  <div
                    className={`w-13 h-13 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                      i === 0
                        ? "bg-[#FF5B1F] text-black"
                        : i === STAGES.length - 1
                          ? "bg-[#F5F0E6] text-black"
                          : "bg-[#0B0A09] border border-[#F5F0E6]/20 text-[#F5F0E6]/80"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className="mt-3 text-xs sm:text-sm tracking-tight font-medium"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {s}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Demo cards */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3 text-xs">
            {[
              { title: "Hook test #14", platform: "REELS", color: "#FF5B1F" },
              { title: "Why I quit FAANG", platform: "YT LONG", color: "#FF5B1F" },
              { title: "Storyboard v2", platform: "YT LONG", color: "#FF5B1F" },
              { title: "Editing — retention pass", platform: "REELS", color: "#F5F0E6" },
              { title: "Drop Tuesday 8 AM", platform: "BLOG", color: "#F5F0E6" },
              { title: "Tools I used", platform: "TWEET", color: "#F5F0E6" },
            ].map((c, i) => (
              <div
                key={i}
                className="rounded-2xl border border-[#F5F0E6]/10 bg-[#0B0A09] p-3 hover:border-[#FF5B1F]/60 transition"
              >
                <div className="text-[10px] tracking-[0.22em] uppercase font-medium" style={{ color: c.color }}>
                  {c.platform}
                </div>
                <div className="mt-3 font-medium leading-snug">{c.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial body */}
      <section className="max-w-[1280px] mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#FF5B1F] mb-4">Manifesto</div>
            <h3 className="text-3xl md:text-4xl tracking-tight leading-tight" style={{ fontFamily: "'Fraunces', serif" }}>
              We don&apos;t need <em className="text-[#FF5B1F] not-italic">more</em> tools.
              <br />We need a <em>home</em>.
            </h3>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 text-lg text-[#F5F0E6]/70 leading-relaxed space-y-5">
            <p>
              You don&apos;t edit in Notion. You don&apos;t storyboard in Trello. You don&apos;t track streaks in
              a spreadsheet. So why do you start your day in eight different tabs?
            </p>
            <p>
              PintaFlow is one calm surface for the entire arc — the spark, the script, the swipe file, the
              calendar, the win. <span className="text-[#FF5B1F]">A studio you actually want to open in the morning.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Feature index */}
      <section className="max-w-[1280px] mx-auto px-6 pb-24">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F5F0E6]/40 mb-6">The Index</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-[#F5F0E6]/10">
          {[
            ["01", "Idea Vault", "Capture, tag, never lose a spark."],
            ["02", "Swipe File", "Save what inspires. Re-find it instantly."],
            ["03", "Pipeline", "Six stages × five formats × your rhythm."],
            ["04", "Calendar", "A publishing month, on one page."],
            ["05", "Streak", "Quiet consistency, visible at a glance."],
            ["06", "Goals", "Weekly, monthly, your own."],
          ].map(([n, t, d]) => (
            <div key={t} className="flex gap-5 py-6 border-b border-[#F5F0E6]/10 group hover:bg-[#100E0C] transition px-2 -mx-2 rounded">
              <span className="text-[#FF5B1F] font-semibold text-sm pt-1">{n}</span>
              <div className="flex-1">
                <div
                  className="text-xl tracking-tight"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  {t}
                </div>
                <div className="text-sm text-[#F5F0E6]/55 mt-1">{d}</div>
              </div>
              <ArrowRight size={16} className="text-[#F5F0E6]/30 group-hover:text-[#FF5B1F] group-hover:translate-x-1 transition mt-1" />
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#F5F0E6]/10 py-10">
        <div className="max-w-[1280px] mx-auto px-6 flex flex-wrap justify-between text-xs text-[#F5F0E6]/40">
          <span>© 2026 PintaFlow — A studio for creators.</span>
          <span style={{ fontFamily: "'Fraunces', serif" }} className="italic">direction · studio orange · v2</span>
        </div>
      </footer>
    </div>
  );
}
